import React from 'react';
import PropTypes from 'prop-types';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import clsx from 'clsx';
import { parse, stringify } from 'query-string';
import { useMediaQuery, Grid, Link, Typography } from '@material-ui/core';
import ExpandMore from '@material-ui/icons/ExpandMore';

// CONSTANTS
import {
  ROUTE_SHOWS_AND_MOVIES_DISCOVER,
  ROUTE_SHOWS_AND_MOVIES_MOVIE,
  ROUTE_SHOWS_AND_MOVIES_SHOW,
} from '@/constants/routes';
import { contentStatuses, ContentType } from '@/constants/watchList';

// UTILITY
import { analyticsAddedToWatchlist } from '@/utility/analytics';
import getLink from '@/utility/routes';

// STORE
import { ContentActions, WatchListActions } from '@/store/actions';

// HOC COMPONENTS
import CheckAuth from '@/hoc/CheckAuth';

// COMPONENTS
import RouterLink from '@/components/Link';
import SearchCardManager from '@/components/Cards/SearchCardManager';
import ShowMovieCard from '@/components/Cards/ShowMovieCard';

// HELP
import { Container } from '../styles';
import useActions from '@/hooks/useActions';

const ShowsMoviesSection = ({ className, data, onUpdateStatus, analyticsLocation, ...props }) => {
  const [t] = useTranslation();
  const location = useLocation();

  const downXs = useMediaQuery(({ breakpoints }) => breakpoints.down('xs'));

  const { user, partner, cActionLoadings } = useSelector(
    ({ auth, partner: partnerStore, watchList, content }) => ({
      user: auth.user,
      partner: partnerStore.data,
      cActionLoadings: { ...watchList.loadings.actions, ...content.loadings.actions },
    }),
  );

  const [changeContentStatus, openAddToWLDialogState] = useActions([
    (content_id, status, callback) => {
      return ContentActions.changeContentStatus.action({
        content_id,
        status,
        callback,
      });
    },
    dialogProps => {
      return WatchListActions.manageAddToWLDialogState.action({
        state: true,
        props: dialogProps,
      });
    },
  ]);

  const isMovie = data.type === ContentType.MOVIE;

  function handleChangeContentStatus(content_id, status) {
    changeContentStatus(content_id, status, () => {
      onUpdateStatus(content_id, status);
    });
  }

  function getSeeAllLink() {
    const locaitonQueryParams = parse(location.search, {
      arrayFormat: 'comma',
      skipEmptyString: true,
      parseNumbers: true,
    });

    const params = {
      ...locaitonQueryParams,
      type: [isMovie ? ContentType.MOVIE_ID : ContentType.SHOW_ID],
      appsType: 2,
    };

    const search = stringify(params, {
      arrayFormat: 'comma',
      skipEmptyString: true,
    });

    return `${ROUTE_SHOWS_AND_MOVIES_DISCOVER}?${search}`;
  }

  function getSeeAllTitle() {
    return t(`common.${isMovie ? 'showAllMovies' : 'showAllTVShows'}`);
  }

  function getGenresLink() {
    const genreIds = data.genres.map(genre => genre.genre_id);
    const uniqueGenreIds = [...new Set(genreIds)];
    const params = {
      genres: uniqueGenreIds,
      type: [isMovie ? ContentType.MOVIE_ID : ContentType.SHOW_ID],
      appsType: 2,
    };

    const search = stringify(params, {
      arrayFormat: 'comma',
      skipEmptyString: true,
    });

    return `${ROUTE_SHOWS_AND_MOVIES_DISCOVER}?${search}`;
  }

  function getGenresTitle() {
    const genreNames = data.genres.map(genre => genre.name);
    const uniqueGenreNames = [...new Set(genreNames)];

    return (
      <Trans
        i18nKey={`common.${
          uniqueGenreNames.length === 1 ? 'seeEntityInCategory' : 'seeEntityInString'
        }`}
        values={{
          string: uniqueGenreNames.map(genre => `<b>${genre}</b>`).join(' | '),
          entity: t(`common.${isMovie ? 'movies' : 'tvShows'}`),
        }}
        components={{
          b: <Typography variant="h6" component="b" color="inherit" />,
        }}
      />
    );
  }

  const seeAllData = React.useMemo(() => {
    if (!data.show_more) return null;

    const link = getSeeAllLink();
    const title = getSeeAllTitle();

    return { link, title };
  }, [data.show_more]);

  const genresData = React.useMemo(() => {
    if (!data.genres.length) return null;

    const link = getGenresLink();
    const title = getGenresTitle();

    return { link, title };
  }, [data.genres]);

  function handleChangeWatchList(contentData) {
    const { id, title } = contentData;

    function addCallback() {
      const properties = {
        content_id: id,
        content_name: title,
        user_id: user?.id || '',
        partner_id: partner.id || '',
        location: analyticsLocation,
      };

      analyticsAddedToWatchlist(properties);
    }

    openAddToWLDialogState({
      data: contentData,
      addCallback,
    });
  }

  function getActions(contentData) {
    const link = getLink(isMovie ? ROUTE_SHOWS_AND_MOVIES_MOVIE : ROUTE_SHOWS_AND_MOVIES_SHOW, {
      movie_id: contentData.slug || contentData.id,
      show_id: contentData.slug || contentData.id,
    });
    const inWatchlist = !!contentData.in_watchlist;
    const contentStatus = contentData.user_content_status;
    const loading = !!cActionLoadings[contentData.id];

    return [
      {
        title: t('buttons.view'),
        onClick: () => {},
        component: RouterLink,
        to: link,
        location: analyticsLocation,
      },
      {
        title: inWatchlist ? t('buttons.removeFromWatchlist') : t('buttons.addToWatchlist'),
        onClick: () => handleChangeWatchList(contentData),
        hoc: CheckAuth,
        disabled: loading,
      },
      {
        title:
          contentStatus === contentStatuses.watched
            ? t('buttons.removeFromWatched')
            : t('buttons.watched'),
        onClick: () => {
          const isAdded = contentStatus === contentStatuses.watched;

          handleChangeContentStatus(contentData.id, isAdded ? null : contentStatuses.watched);
        },
        hoc: CheckAuth,
      },
      {
        title:
          contentStatus === contentStatuses.notInterested
            ? t('buttons.removeFromNotInterested')
            : t('buttons.notInterested'),
        onClick: () => {
          const isAdded = contentStatus === contentStatuses.notInterested;

          handleChangeContentStatus(contentData.id, isAdded ? null : contentStatuses.notInterested);
        },
        hoc: CheckAuth,
      },
    ];
  }

  return (
    <Container
      {...props}
      className={clsx('section-manager', 'section-manager-shows-movies', className)}
    >
      <Typography className="section-manager__title" variant="h3">
        {t(`common.${isMovie ? 'movies' : 'tvShows'}`)}
      </Typography>
      <Grid container spacing={2}>
        {genresData && (
          <Grid item xs={12}>
            <Link
              className="section-manager__link-genres"
              component={RouterLink}
              to={genresData.link}
              color="textPrimary"
            >
              {genresData.title}
            </Link>
          </Grid>
        )}
        {data.data.map((item, idx) => (
          <Grid item xs={6} sm={12} key={idx}>
            {downXs ? (
              <RouterLink
                to={getLink(isMovie ? ROUTE_SHOWS_AND_MOVIES_MOVIE : ROUTE_SHOWS_AND_MOVIES_SHOW, {
                  movie_id: item.slug || item.id,
                  show_id: item.slug || item.id,
                })}
              >
                <ShowMovieCard
                  data={item}
                  tooltip={false}
                  analyticsLocation={analyticsLocation}
                  partner={partner}
                />
              </RouterLink>
            ) : (
              <SearchCardManager
                type={data.type}
                data={item}
                actions={getActions(item)}
                analyticsLocation={analyticsLocation}
              />
            )}
          </Grid>
        ))}
      </Grid>
      {seeAllData && (
        <div className="section-manager__link">
          <Link component={RouterLink} to={seeAllData.link} color="inherit">
            {seeAllData.title}{' '}
            <ExpandMore className="section-manager__link-icon" fontSize="inherit" color="inherit" />
          </Link>
        </div>
      )}
    </Container>
  );
};

ShowsMoviesSection.defaultProps = {
  className: '',
  onUpdateStatus: () => {},
  analyticsLocation: '',
};

ShowsMoviesSection.propTypes = {
  data: PropTypes.object.isRequired,
  className: PropTypes.string,
  onUpdateStatus: PropTypes.func,
  analyticsLocation: PropTypes.string,
};

export default React.memo(ShowsMoviesSection);
